<template>
<Modal
    v-if="isModalActive"
    :isActive.sync="isModalActive"
    :isCloseable="isModalCloseable"
    class="place-bounty-modal">
    <template v-if="viewState === VIEW_STATES.USED_BITS">
        <span v-if="isLoading">
            Waiting for confirmation...
        </span>
        <div v-else-if="isTransactionSettled">
            <p v-if="transactingBountyState === $const.STATE_SUCCESS">
                <FaIcon class="has-text-success m-r-5" icon="check"/>
                Thanks for your support!
            </p>
            <template v-else-if="transactingBountyState === $const.STATE_ERROR">
                <p>
                    <FaIcon class="has-text-danger m-r-5" icon="times"/>
                    Error
                </p>
                <p>
                    Sorry. Your bits transaction was confirmed by Twitch, but
                    something went wrong with the extension.
                </p>
                <p>
                    Send this information to my email
                    at guanzo91@gmail.com, and I'll fix it.
                </p>
                <p v-for="(val, key) in errorReportDetails" :key="key">
                    {{ key }}<br>{{ val }}
                </p>
            </template>
            <button
                @click="onUserConfirmTransactionResult"
                class="button is-success is-small is-fullwidth">
                Okay
            </button>
        </div>
    </template>
    <template v-else>
        <h5 class="subtitle is-5">Show support</h5>
        <p>
            How much do you want the streamer to complete this suggestion?
            <a @click="showDetails = !showDetails">
                {{ showDetails ? 'Back' : 'Details' }}
            </a>
        </p>
        <div v-if="showDetails" class="content">
            <ul>
                <li>
                    Your username and bits amount will appear on a leaderboard.
                </li>
                <li>
                    You can use unlimited bits on a single suggestion.
                </li>
                <li>
                    You can use bits on any suggestion.
                </li>
            </ul>
        </div>
        <template v-else>
            <table class="table is-narrow is-hoverable is-fullwidth">
                <thead>
                    <tr>
                        <th>Level of support</th>
                        <th class="bounty-amount">Bits</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(bounty, i) in bountyProducts"
                        @mouseover="$store.dispatch('showBitsBalance')"
                        @click="selectedBounty = bounty"
                        :class="{ 'is-active': selectedBounty === bounty}"
                        :key="bounty.sku">
                        <td class="bounty-name">
                            {{ bounty.displayName }}
                        </td>
                        <td class="bounty-amount">
                            {{ bounty.cost.amount.toLocaleString() }}
                        </td>
                        <td class="bounty-image">
                            <img :src="getProductImage(i)"/>
                        </td>
                    </tr>
                </tbody>
            </table>
            <button
                @click="startBountyTransaction"
                v-bind="submitBtnAttrs"
                class="submit-bounty button is-success is-small is-fullwidth">
                Submit
            </button>
        </template>
    </template>
</Modal>
</template>

<script>
import { mapGetters } from 'vuex'

import * as C from '@shared/constants'
import * as M from '@/store/mutations'
import bitsImages from '@/bits-images'

const BOUNTY_SKU_PREFIX = 'BOUNTY_'

const VIEW_STATES = {
    DEFAULT: 'DEFAULT',
    SHOW_PRODUCTS: 'SHOW_PRODUCTS',
    USED_BITS: 'USED_BITS',
}

export default {
    name: 'BountyModal',
    props: {
        suggestion: Object,
    },
    data: vm => ({
        showDetails: false,
        selectedBounty: vm.$store.getters.bitsProducts[0],
        lastAnimationDate: null,
        viewState: VIEW_STATES.DEFAULT,
        VIEW_STATES,
    }),
    computed: {
        ...mapGetters([
            'bitsProducts',
            'transactingBountySuggestionId',
            'transactingBountyState',
            'completedTransaction',
            'isPageViewer',
        ]),
        isModalActive: {
            get: vm => vm.viewState !== VIEW_STATES.DEFAULT,
            set (isActive) {
                if (!isActive) {
                    this.transition(VIEW_STATES.DEFAULT)
                }
            }
        },
        bountyProducts: vm => vm.bitsProducts.filter(
            p => p.sku.startsWith(BOUNTY_SKU_PREFIX)
        ),
        // Returns true if this suggestion is the one being transacted.
        hasActiveTransaction: vm => (
            vm.suggestion.id === vm.transactingBountySuggestionId
        ),
        isLoading: vm => (
            vm.hasActiveTransaction && vm.transactingBountyState === C.STATE_LOADING
        ),
        isTransactionSettled: vm => (
            vm.hasActiveTransaction &&
            [C.STATE_SUCCESS, C.STATE_ERROR].includes(vm.transactingBountyState)
        ),
        // user cannot close modal via closeBtn/clickOutside while:
        // - the twitch bits dialog is open
        // - the transaction is being saved to the EBS.
        // - the transaction errored (user must click ok button)
        isModalCloseable: vm => !vm.isLoading && vm.transactingBountyState !== C.STATE_ERROR,
        // useBits() only works on the viewer page.
        submitBtnAttrs: vm => ({
            disabled: !vm.isPageViewer,
            title: !vm.isPageViewer ? "You can't use bits on this page" : null,
        }),
        errorReportDetails: vm => {
            const t = vm.completedTransaction

            return {
                'Transaction Id': t.transactionId,
                'Suggestion Id': vm.suggestion.id,
            }
        }
    },
    watch: {
        hasActiveTransaction () {
            if (!this.hasActiveTransaction) {
                if (this.viewState === VIEW_STATES.USED_BITS) {
                    this.transition(VIEW_STATES.SHOW_PRODUCTS)
                }
            }
        }
    },
    methods: {
        open () {
            this.transition(VIEW_STATES.SHOW_PRODUCTS)
        },
        async startBountyTransaction () {
            const { id } = this.suggestion
            const payload = { suggestionId: id, state: C.STATE_LOADING }
            this.$store.commit(M.SET_TRANSACTING_BOUNTY_STATE, payload)
            this.$store.dispatch('useBits', { sku: this.selectedBounty.sku })

            this.transition(VIEW_STATES.USED_BITS)
        },
        onUserConfirmTransactionResult () {
            const payload = { state: C.STATE_DEFAULT }
            this.$store.commit(M.SET_TRANSACTING_BOUNTY_STATE, payload)

            this.transition(VIEW_STATES.DEFAULT)
        },
        getProductImage: index => bitsImages[index].images.static[1],
        transition (to) {
            this.viewState = to
        }
    },
    components: {
    }
}
</script>

<style lang="scss" scoped>
.table {
    margin-bottom: 0;
    .bounty-amount {
        text-align: right;
    }
    .bounty-image {
        padding-right: 0;
        img {
            vertical-align: top;
            width: 20px;
            height: auto;
        }
    }
    tbody tr {
        cursor: pointer;
        &.is-active {
            border-left: 4px solid $green;
        }
        td {
            font-size: 0.9rem;
            vertical-align: middle;
        }
    }
}
.submit-bounty {
    text-transform: capitalize;
}

</style>
